import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Card, CardActionArea, CardContent, Typography, Box, Skeleton } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { LEADS_PATH } from '../../navigations/path';

function StatusCardSkeleton() {
  return (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2} sx={{ minWidth: 0 }}>
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton animation="wave" height={24} width="70%" />
          <Skeleton animation="wave" height={36} width="40%" sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    </Grid>
  );
}

export function buildLeadsStatusQuery(status) {
  const params = new URLSearchParams();
  const statusName = String(status?.statusName || status?.label || status?.status || '').trim();
  const statusId = String(status?.statusId || '').trim();

  // Deep-link with a single param — leads list API filters by status_id only
  if (statusId) {
    params.set('status_id', statusId);
  } else if (statusName) {
    params.set('status_name', statusName);
  }

  return params.toString();
}

const DashboardStatusCards = ({
  title = 'Status Overview',
  items = [],
  loading = false,
  /** Optional: filter in-place (lead list). Default navigates to /leads?status_id=… */
  onStatusClick,
  selectedStatusId = '',
}) => {
  const navigate = useNavigate();

  const handleStatusClick = (status) => {
    if (typeof onStatusClick === 'function') {
      onStatusClick(status);
      return;
    }
    const query = buildLeadsStatusQuery(status);
    if (!query) return;
    navigate(`${LEADS_PATH}?${query}`);
  };

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton animation="wave" height={28} width={220} sx={{ mb: 1.5 }} />
        <Grid container spacing={2}>
          {[...Array(5)].map((_, index) => (
            <StatusCardSkeleton key={index} />
          ))}
        </Grid>
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No status metrics available for your role.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {items.map((status) => {
          const isSelected =
            selectedStatusId != null &&
            selectedStatusId !== '' &&
            String(selectedStatusId) === String(status.statusId || status.id);

          return (
            <Grid key={status.id} item xs={12} sm={6} md={4} lg={3} xl={2} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  height: '100%',
                  border: isSelected ? 2 : 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleStatusClick(status)}
                  sx={{ height: '100%', alignItems: 'stretch' }}
                  aria-label={`View leads with status ${status.label}`}
                  aria-pressed={isSelected}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary" noWrap title={status.label}>
                          {status.label}
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {status.count}
                        </Typography>
                      </Box>
                      <AssessmentIcon color={isSelected ? 'primary' : 'info'} />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default DashboardStatusCards;
